/**
 * A "virtual" link that represents an auto-generated button (chatbot or contact form).
 * Unlike real links, virtual links are not stored in the `links` table — their presence
 * and appearance depend on separate settings.  Only their position inside the links list
 * is persisted (chat_agents.bio_link_position / page_settings.contact_link_position).
 *
 * Position semantics: integer N means the visual index of the virtual link
 * inside the combined ordered stack shown in the editor/public bio preview
 * (0 = first item, 1 = second item, etc.). `null` means append using the
 * legacy fallback behavior.
 */
export interface VirtualLink {
  /** Discriminator so callers can distinguish from real Link objects. */
  _virtual: 'chat' | 'contact' | 'store';
  /** Stable ID used as Reorder.Item key and in reorder callbacks. */
  id: '__chat__' | '__contact__' | '__store__';
  /** Display label shown in the editor row. */
  title: string;
  /** See JSDoc above — null means end-of-list / legacy fallback. */
  position: number | null;
  /** Whether this virtual feature is currently active/enabled on the public bio. */
  is_active?: boolean;
  /** True when the user's current plan does not include this feature (show locked CTA). */
  locked?: boolean;
  /** The minimum plan slug needed to unlock this feature (used in the CTA label). */
  requiredPlan?: string;
  /** How the virtual chat link renders on the public bio. */
  displayMode?: 'button' | 'icon' | 'widget';
  button_color?: string | null;
  button_text_color?: string | null;
  button_gradient_type?: 'solid' | 'linear' | 'radial' | 'conic';
  button_gradient_end?: string | null;
  button_gradient_angle?: number;
  use_global_theme?: boolean;
  widget_icon?: string | null;
  widget_icon_url?: string | null;
  widget_color?: string | null;
  widget_text_color?: string | null;
  widget_size?: 'small' | 'medium' | 'large';
  avatar_url?: string | null;
}

export interface VirtualLinkReorderUpdate {
  id: '__chat__' | '__contact__' | '__store__';
  /** Updated visual position inside the combined rendered list. */
  position: number;
}

export interface Link {
  id: string;
  user_id: string;
  title: string;
  url: string;
  icon: string | null;
  image_url: string | null;
  image_position: 'top' | 'left' | 'right' | 'bottom';
  position: number;
  is_active: boolean;
  button_color: string | null;
  button_text_color: string | null;
  button_gradient_type: 'solid' | 'linear' | 'radial' | 'conic';
  button_gradient_end: string | null;
  button_gradient_angle: number;
  use_global_theme: boolean;
  scheduled_publish_at: string | null;
  scheduled_expire_at: string | null;
  ab_testing_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ABTestVariant {
  id: string;
  link_id: string;
  variant_name: string;
  title: string;
  url: string;
  icon: string | null;
  button_color: string | null;
  button_text_color: string | null;
  traffic_percentage: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ABTestStats {
  variant_id: string;
  variant_name: string;
  views_count: number;
  clicks_count: number;
  ctr: number;
  traffic_percentage: number;
}

export const LINK_ICON_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'book-open', label: 'Blog' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'message-circle', label: 'Chat' },
  { value: 'coffee', label: 'Coffee' },
  { value: 'file-text', label: 'Document' },
  { value: 'download', label: 'Download' },
  { value: 'mail', label: 'Email' },
  { value: 'gift', label: 'Gift' },
  { value: 'github', label: 'GitHub' },
  { value: 'globe', label: 'Globe' },
  { value: 'heart', label: 'Heart' },
  { value: 'image', label: 'Image' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'link', label: 'Link' },
  { value: 'map-pin', label: 'Location' },
  { value: 'mic', label: 'Mic' },
  { value: 'music', label: 'Music' },
  { value: 'phone', label: 'Phone' },
  { value: 'circle-play', label: 'Play' },
  { value: 'headphones', label: 'Podcast' },
  { value: 'shopping-bag', label: 'Shop' },
  { value: 'star', label: 'Star' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'video', label: 'Video' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'zap', label: 'Zap' },
] as const;

export const IMAGE_POSITIONS = [
  { value: 'top', label: 'Top' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'bottom', label: 'Bottom' },
] as const;

/** Shape of the avatar container — independent from the border effect. */
export type AvatarShape =
  | 'circle' | 'square'
  | 'hexagon' | 'diamond' | 'pentagon'
  | 'star' | 'star6'
  | 'octagon' | 'shield'
  | 'cross' | 'arrow' | 'triangle'
  | 'parallelogram' | 'chevron' | 'flag'
  | 'bevel' | 'notch' | 'rabbet';

/**
 * Border/effect applied on top of the avatar shape.
 * Shape-named values kept for backward compat with old data; new UI uses avatar_shape separately.
 */
export type AvatarFrame =
  // effects
  | 'none'
  | 'ring' | 'thick' | 'double-ring'
  | 'glow' | 'neon' | 'soft-glow'
  | 'dashed' | 'dots'
  | 'inset-ring' | 'shadow' | 'hard-shadow'
  | 'groove' | 'ridge'
  | 'multi-ring'
  | 'outline' | 'minimal' | 'photo-frame'
  | 'vignette' | 'retro'
  | 'pulse-ring' | 'dbl-shadow' | 'border-glow'
  // legacy shape values (backward compat — use avatar_shape field instead)
  | 'square'
  | 'hexagon' | 'diamond' | 'pentagon'
  | 'star' | 'star6'
  | 'octagon' | 'shield'
  | 'cross' | 'arrow' | 'triangle'
  | 'parallelogram' | 'chevron' | 'flag'
  | 'bevel' | 'notch' | 'rabbet'
  // gradients
  | 'gradient'
  | 'gradient-sunset' | 'gradient-ocean' | 'gradient-gold'
  | 'gradient-galaxy' | 'gradient-forest'
  | 'gradient-aurora' | 'gradient-rose' | 'gradient-fire' | 'gradient-candy'
  | 'gradient-mint' | 'gradient-dusk' | 'gradient-peach' | 'gradient-electric'
  | 'gradient-lemon' | 'gradient-berry' | 'gradient-nordic' | 'gradient-mango'
  | 'gradient-lavender' | 'gradient-teal' | 'gradient-copper' | 'gradient-midnight'
  | 'gradient-spring' | 'gradient-neon-cyan' | 'gradient-bronze' | 'gradient-ice';

export interface PageSettings {
  id: string;
  user_id: string;
  theme: string;
  background_mode: 'preset' | 'custom';
  background_type: 'solid' | 'gradient' | 'image';
  background_color: string;
  background_gradient_start: string | null;
  background_gradient_end: string | null;
  background_image_url: string | null;
  background_video_url: string | null;
  background_video_muted: boolean;
  background_video_loop: boolean;
  background_video_fit: 'cover' | 'contain';
  background_overlay_enabled: boolean;
  background_overlay_color: string;
  background_overlay_opacity: number;
  background_overlay_source: 'custom' | 'theme';
  /** @deprecated Use button_shape / button_border_style / button_fill / button_shadow */
  button_style?: ButtonStyleValue;
  button_shape: ButtonShape;
  button_border_style: ButtonBorderStyle;
  button_fill: ButtonFill;
  button_shadow: ButtonShadow;
  button_shadow_color: string;
  button_shadow_x: number;
  button_shadow_y: number;
  button_shadow_blur: number;
  button_shadow_spread: number;
  /** When true, button colors are derived from text_color / background_color (theme-native). */
  button_use_theme_colors: boolean;
  button_color: string;
  button_text_color: string;
  /** Explicit border color. Empty string = derive from button text color. */
  button_border_color: string;
  font_family: string;
  text_color: string;
  show_avatar: boolean;
  /** Clip-path shape of the avatar. Default 'circle'. */
  avatar_shape: AvatarShape;
  avatar_frame: AvatarFrame;
  /** Frame color override. Empty string = derive from button color. */
  avatar_frame_color: string;
  show_bio: boolean;
  store_link_enabled: boolean;
  store_name: string;
  store_button_text: string;
  store_display_mode: 'pagination' | 'infinite_scroll';
  store_products_per_page: number;
  store_search_enabled: boolean;
  email_collection_enabled: boolean;
  email_collection_title: string;
  email_collection_description: string;
  email_collection_button_text: string;
  email_collection_position: 'top' | 'bottom' | 'both';
  email_collection_display_mode: 'button' | 'icon';
  /** Position of the contact-form button in the combined rendered list. */
  contact_link_position: number | null;
  /** Position of the store button in the combined rendered list. */
  store_link_position: number | null;
  /** When true, the "Feito com Qlivar" footer is hidden on the public bio page. */
  hide_branding: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedThemeSettings {
  theme: string;
  background_mode: 'preset' | 'custom';
  background_type: 'solid' | 'gradient' | 'image';
  background_color: string;
  background_gradient_start: string | null;
  background_gradient_end: string | null;
  background_image_url: string | null;
  background_video_url?: string | null;
  background_video_muted?: boolean;
  background_video_loop?: boolean;
  background_video_fit?: 'cover' | 'contain';
  background_overlay_enabled: boolean;
  background_overlay_color: string;
  background_overlay_opacity: number;
  background_overlay_source: 'custom' | 'theme';
  /** @deprecated Use button_shape / button_border_style / button_fill / button_shadow */
  button_style?: ButtonStyleValue;
  button_shape?: ButtonShape;
  button_border_style?: ButtonBorderStyle;
  button_fill?: ButtonFill;
  button_shadow?: ButtonShadow;
  button_shadow_color?: string;
  button_shadow_x?: number;
  button_shadow_y?: number;
  button_shadow_blur?: number;
  button_shadow_spread?: number;
  button_use_theme_colors?: boolean;
  button_color: string;
  button_text_color: string;
  button_border_color?: string;
  font_family: string;
  text_color: string;
  show_avatar: boolean;
  avatar_shape?: AvatarShape;
  avatar_frame?: AvatarFrame;
  avatar_frame_color?: string;
  show_bio: boolean;
}

export interface CustomTheme {
  id: string;
  user_id: string;
  name: string;
  settings: SavedThemeSettings;
  created_at: string;
  updated_at: string;
}

export interface EmailSubscriber {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  source: string;
  status: 'active' | 'unsubscribed' | 'bounced';
  metadata: Record<string, unknown>;
  subscribed_at: string;
  unsubscribed_at: string | null;
  created_at: string;
}

export interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  full_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  /** ISO timestamp of the last username change. Used to enforce 7-day cooldown. */
  username_last_changed_at?: string | null;
}

/**
 * Return type for profile update operations.
 * - `true`        — saved successfully
 * - `false`       — username already taken by another user, or a generic DB error
 * - `'cooldown'`  — blocked by the 7-day username-change cooldown
 */
export type ProfileUpdateResult = boolean | 'cooldown';

export interface PreviewToken {
  id: string;
  user_id: string;
  token: string;
  expires_at: string;
  created_at: string;
  last_used_at: string | null;
  uses_count: number;
}

export type ButtonShape       = 'rounded' | 'pill' | 'square';
export type ButtonBorderStyle = 'none' | 'solid' | 'dashed' | 'dotted' | 'sketch' | 'chalk';
export type ButtonFill        = 'filled' | 'outline';
export type ButtonShadow      = 'none' | 'flat' | 'soft';

/** @deprecated Replaced by ButtonShape + ButtonBorderStyle + ButtonFill + ButtonShadow */
export type ButtonStyleValue =
  | 'rounded' | 'pill' | 'square'
  | 'outline' | 'outline-pill' | 'outline-square'
  | 'filled-border' | 'filled-border-pill' | 'filled-border-square'
  | 'shadow' | 'shadow-pill' | 'shadow-square'
  | 'dashed' | 'dashed-pill' | 'dashed-square'
  | 'dotted' | 'dotted-pill' | 'dotted-square'
  | 'sketch' | 'sketch-filled'
  | 'chalk' | 'chalk-filled';

export type ButtonStyleGroup = 'filled' | 'outline' | 'filled-border' | 'shadow' | 'dashed' | 'dotted' | 'sketch' | 'chalk';

export interface ButtonStyleDef {
  value: ButtonStyleValue;
  label: string;
  class: string;
  group: ButtonStyleGroup;
}

export const BUTTON_STYLES: ButtonStyleDef[] = [
  // ── Preenchido ─────────────────────────────────────────────────────────────────────────────
  { value: 'rounded',           label: 'Arredondado', class: 'rounded-xl',                          group: 'filled'        },
  { value: 'pill',              label: 'Pílula',      class: 'rounded-full',                        group: 'filled'        },
  { value: 'square',            label: 'Quadrado',    class: 'rounded-none',                        group: 'filled'        },
  // ── Contorno ───────────────────────────────────────────────────────────────────────────────
  { value: 'outline',           label: 'Arredondado', class: 'rounded-xl border-2',                 group: 'outline'       },
  { value: 'outline-pill',      label: 'Pílula',      class: 'rounded-full border-2',               group: 'outline'       },
  { value: 'outline-square',    label: 'Quadrado',    class: 'rounded-none border-2',               group: 'outline'       },
  // ── Preenchido + borda ─────────────────────────────────────────────────────────────
  { value: 'filled-border',      label: 'Arredondado', class: 'rounded-xl border-2',                 group: 'filled-border' },
  { value: 'filled-border-pill', label: 'Pílula',      class: 'rounded-full border-2',               group: 'filled-border' },
  { value: 'filled-border-square',label:'Quadrado',    class: 'rounded-none border-2',               group: 'filled-border' },
  // ── Sombra sólida ─────────────────────────────────────────────────────────────────────
  { value: 'shadow',             label: 'Arredondado', class: 'rounded-xl border-2',                 group: 'shadow'        },
  { value: 'shadow-pill',        label: 'Pílula',      class: 'rounded-full border-2',               group: 'shadow'        },
  { value: 'shadow-square',      label: 'Quadrado',    class: 'rounded-none border-2',               group: 'shadow'        },
  // ── Tracejado ──────────────────────────────────────────────────────────────────────────────
  { value: 'dashed',             label: 'Arredondado', class: 'rounded-xl border-2 border-dashed',   group: 'dashed'        },
  { value: 'dashed-pill',        label: 'Pílula',      class: 'rounded-full border-2 border-dashed', group: 'dashed'        },
  { value: 'dashed-square',      label: 'Quadrado',    class: 'rounded-none border-2 border-dashed', group: 'dashed'        },
  // ── Pontilhado ─────────────────────────────────────────────────────────────────────────────
  { value: 'dotted',             label: 'Arredondado', class: 'rounded-xl border-[3px] border-dotted',    group: 'dotted'        },
  { value: 'dotted-pill',        label: 'Pílula',      class: 'rounded-full border-[3px] border-dotted',  group: 'dotted'        },
  { value: 'dotted-square',      label: 'Quadrado',    class: 'rounded-none border-[3px] border-dotted',  group: 'dotted'        },
  // ── Rabisco ────────────────────────────────────────────────────────────────────────────────
  { value: 'sketch',             label: 'Contorno',    class: 'btn-sketch',                           group: 'sketch'        },
  { value: 'sketch-filled',      label: 'Preenchido',  class: 'btn-sketch-filled',                    group: 'sketch'        },
  // ── Giz ──────────────────────────────────────────────────────────────────────────────────────
  { value: 'chalk',              label: 'Contorno',    class: 'btn-chalk',                            group: 'chalk'         },
  { value: 'chalk-filled',       label: 'Preenchido',  class: 'btn-chalk-filled',                     group: 'chalk'         },
];

export type FontCategory = 'sans' | 'readable' | 'serif' | 'mono' | 'display';

export interface FontDef {
  value: string;
  label: string;
  category: FontCategory;
  /** Short category label shown below the font name in the picker */
  tag: string;
}

export const FONTS: FontDef[] = [
  // ── Sans-serif ────────────────────────────────────────────────────────────
  { value: 'Inter',             label: 'Inter',             category: 'sans',    tag: 'moderno'     },
  { value: 'Poppins',           label: 'Poppins',           category: 'sans',    tag: 'geométrico'  },
  { value: 'Roboto',            label: 'Roboto',            category: 'sans',    tag: 'neutro'      },
  { value: 'Nunito',            label: 'Nunito',            category: 'sans',    tag: 'arredondado' },
  { value: 'DM Sans',           label: 'DM Sans',           category: 'sans',    tag: 'limpo'       },
  { value: 'Outfit',            label: 'Outfit',            category: 'sans',    tag: 'moderno'     },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans', category: 'sans',    tag: 'clean'       },
  { value: 'Montserrat',        label: 'Montserrat',        category: 'sans',    tag: 'geométrico'  },
  { value: 'Raleway',           label: 'Raleway',           category: 'sans',    tag: 'elegante'    },
  { value: 'Manrope',           label: 'Manrope',           category: 'sans',    tag: 'moderno'     },
  // ── Legíveis / leitura ───────────────────────────────────────────────────
  { value: 'Open Sans',         label: 'Open Sans',         category: 'readable', tag: 'leitura'     },
  { value: 'Source Sans 3',     label: 'Source Sans 3',     category: 'readable', tag: 'leitura'     },
  { value: 'IBM Plex Sans',     label: 'IBM Plex Sans',     category: 'readable', tag: 'técnica'     },
  { value: 'Figtree',           label: 'Figtree',           category: 'readable', tag: 'leitura'     },
  { value: 'Lexend',            label: 'Lexend',            category: 'readable', tag: 'acessível'   },
  // ── Serif ─────────────────────────────────────────────────────────────────
  { value: 'Playfair Display',  label: 'Playfair Display',  category: 'serif',   tag: 'editorial'   },
  { value: 'Lora',              label: 'Lora',              category: 'serif',   tag: 'leitura'     },
  { value: 'Merriweather',      label: 'Merriweather',      category: 'serif',   tag: 'leitura'     },
  { value: 'EB Garamond',       label: 'EB Garamond',       category: 'serif',   tag: 'clássica'    },
  { value: 'Cormorant Garamond',label: 'Cormorant',         category: 'serif',   tag: 'elegante'    },
  { value: 'Libre Baskerville', label: 'Libre Baskerville', category: 'serif',   tag: 'clássica'    },
  { value: 'Crimson Text',      label: 'Crimson Text',      category: 'serif',   tag: 'editorial'   },
  { value: 'Spectral',          label: 'Spectral',          category: 'serif',   tag: 'editorial'   },
  { value: 'Libre Caslon Text', label: 'Libre Caslon',      category: 'serif',   tag: 'clássica'    },
  // ── Monospace ─────────────────────────────────────────────────────────────
  { value: 'Space Grotesk',     label: 'Space Grotesk',     category: 'mono',    tag: 'técnica'     },
  { value: 'Space Mono',        label: 'Space Mono',        category: 'mono',    tag: 'monospace'   },
  { value: 'JetBrains Mono',    label: 'JetBrains Mono',    category: 'mono',    tag: 'monospace'   },
  { value: 'Fira Code',         label: 'Fira Code',         category: 'mono',    tag: 'ligatures'   },
  { value: 'Source Code Pro',   label: 'Source Code Pro',   category: 'mono',    tag: 'monospace'   },
  { value: 'IBM Plex Mono',     label: 'IBM Plex Mono',     category: 'mono',    tag: 'técnica'     },
  // ── Display / personalidade ───────────────────────────────────────────────
  { value: 'Pacifico',          label: 'Pacifico',          category: 'display', tag: 'cursiva'     },
  { value: 'Bebas Neue',        label: 'Bebas Neue',        category: 'display', tag: 'impacto'     },
  { value: 'Caveat',            label: 'Caveat',            category: 'display', tag: 'manuscrita'  },
  { value: 'Josefin Sans',      label: 'Josefin Sans',      category: 'display', tag: 'vintage'     },
  { value: 'Abril Fatface',     label: 'Abril Fatface',     category: 'display', tag: 'impacto'     },
  { value: 'Dancing Script',    label: 'Dancing Script',    category: 'display', tag: 'cursiva'     },
  { value: 'Righteous',         label: 'Righteous',         category: 'display', tag: 'retrô'       },
];

export interface ThemePreset {
  name: string;
  background_type: 'solid' | 'gradient';
  background_color?: string;
  background_gradient_start?: string;
  background_gradient_end?: string;
  button_color: string;
  button_text_color: string;
  text_color: string;
  premium?: boolean;
  /** Display category — defaults derived from background_type if omitted */
  category?: 'solid' | 'gradient' | 'special' | 'animated';
  /** CSS class name that provides an animated background (overrides inline style) */
  animationClass?: string;
  /** Static CSS background shown in the theme-picker thumbnail when the animation is frozen */
  staticPreview?: string;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    name: 'Minimal',
    background_type: 'solid',
    background_color: '#ffffff',
    button_color: '#1a1a2e',
    button_text_color: '#ffffff',
    text_color: '#1a1a2e',
  },
  {
    name: 'Dark',
    background_type: 'solid',
    background_color: '#1a1a2e',
    button_color: '#ffffff',
    button_text_color: '#1a1a2e',
    text_color: '#ffffff',
  },
  {
    name: 'Sand',
    background_type: 'solid',
    background_color: '#f5efe6',
    button_color: '#3f3f46',
    button_text_color: '#ffffff',
    text_color: '#3f3f46',
  },
  {
    name: 'Fluid Neon',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#01050f',
    button_color: '#004cff',
    button_text_color: '#ffffff',
    text_color: '#ffffff',
    animationClass: 'webgl-liquid-neon',
    staticPreview: 'linear-gradient(135deg, #01050f 0%, #003b6e 30%, #00ef82 60%, #0044ff 100%)',
    premium: true,
  },
  {
    name: 'Fluid Acid',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#0a001a',
    button_color: '#00ff80',
    button_text_color: '#000000',
    text_color: '#ffffff',
    animationClass: 'webgl-liquid-acid',
    staticPreview: 'linear-gradient(135deg, #0a001a 0%, #3d0066 40%, #00ff80 70%, #0a001a 100%)',
    premium: true,
  },
  {
    name: 'Fluid Portal',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#081a05',
    button_color: '#55cc22',
    button_text_color: '#000000',
    text_color: '#ffffff',
    animationClass: 'webgl-portal',
    staticPreview: 'radial-gradient(circle at 50% 50%, #1a4d00 0%, #0a2800 40%, #081a05 100%)',
    premium: true,
  },
  {
    name: 'Fluid Pool',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#013255',
    button_color: '#ffffff',
    button_text_color: '#013255',
    text_color: '#ffffff',
    animationClass: 'webgl-pool',
    staticPreview: 'linear-gradient(135deg, #013255 0%, #0077b6 40%, #90e0ef 70%, #013255 100%)',
    premium: true,
  },
  {
    name: 'Cyberpunk Fluid',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#080014',
    button_color: '#6a00ff',
    button_text_color: '#39ff14',
    text_color: '#ffffff',
    animationClass: 'webgl-cyber-fluid',
    staticPreview: 'linear-gradient(135deg, #080014 0%, #6a00ff 40%, #00fff9 70%, #080014 100%)',
    premium: true,
  },
  {
    name: 'Fluid Magma',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#100201',
    button_color: '#ff4500',
    button_text_color: '#ffffff',
    text_color: '#ffffff',
    animationClass: 'webgl-liquid-magma',
    staticPreview: 'linear-gradient(135deg, #100201 0%, #7a0000 30%, #ff4500 60%, #ff8c00 80%, #100201 100%)',
    premium: true,
  },
  {
    name: 'Fluid Ocean',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#000811',
    button_color: '#0088ff',
    button_text_color: '#ffffff',
    text_color: '#ffffff',
    animationClass: 'webgl-liquid-ocean',
    staticPreview: 'linear-gradient(135deg, #000811 0%, #023e8a 40%, #48cae4 70%, #000811 100%)',
    premium: true,
  },
  {
    name: 'Fluid Cotton',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#f5eef8',
    button_color: '#d46b9a',
    button_text_color: '#ffffff',
    text_color: '#342a3f',
    animationClass: 'webgl-liquid-cotton',
    staticPreview: 'linear-gradient(135deg, #f5eef8 0%, #e8b4f8 40%, #ffc8dd 70%, #f5eef8 100%)',
    premium: true,
  },
  {
    name: 'Azure Glow',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#020312',
    button_color: 'rgba(255, 255, 255, 0.1)',
    button_text_color: '#ffffff',
    text_color: '#ffffff',
    animationClass: 'webgl-mesh-azure',
    staticPreview: 'radial-gradient(ellipse at 40% 30%, #1a4f8a 0%, #020312 50%, #00b4d8 100%)',
    premium: true,
  },
  {
    name: 'Sapphire',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#010c20',
    button_color: 'rgba(255, 255, 255, 0.1)',
    button_text_color: '#ffffff',
    text_color: '#ffffff',
    animationClass: 'webgl-liquid-sapphire',
    staticPreview: 'linear-gradient(135deg, #010c20 0%, #1a3a6e 40%, #4479c4 70%, #010c20 100%)',
    premium: true,
  },
  {
    name: 'Bubbles',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#0d0d1f',
    button_color: '#ffffff',
    button_text_color: '#1a1a2e',
    text_color: '#ffffff',
    animationClass: 'liquid-iridescent-bg',
    staticPreview: 'radial-gradient(ellipse at 30% 30%, #6b21a8 0%, #0d0d1f 50%, #1e3a5f 100%)',
    premium: true,
  },
  {
    name: 'Aurora',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#0a0015',
    button_color: '#e0aaff',
    button_text_color: '#10002b',
    text_color: '#ffffff',
    animationClass: 'webgl-iridescent-bg',
    staticPreview: 'linear-gradient(135deg, #0a0015 0%, #7c3aed 30%, #06b6d4 60%, #10b981 100%)',
    premium: true,
  },
  {
    name: 'Northern Lights',
    category: 'animated',
    background_type: 'gradient',
    background_color: '#010a08',
    button_color: '#a8f0c6',
    button_text_color: '#01150e',
    text_color: '#e8fff4',
    animationClass: 'webgl-aurora-borealis-bg',
    staticPreview: 'linear-gradient(135deg, #010a08 0%, #065f46 30%, #00ff87 60%, #0ea5e9 100%)',
    premium: true,
  },
  {
    name: 'Mint',
    background_type: 'solid',
    background_color: '#ecfeff',
    button_color: '#0d9488',
    button_text_color: '#ffffff',
    text_color: '#115e59',
  },
  {
    name: 'Rose',
    background_type: 'solid',
    background_color: '#fce4ec',
    button_color: '#e91e63',
    button_text_color: '#ffffff',
    text_color: '#880e4f',
  },
  {
    name: 'Lavender',
    background_type: 'solid',
    background_color: '#f3e8ff',
    button_color: '#7c3aed',
    button_text_color: '#ffffff',
    text_color: '#4c1d95',
  },
  {
    name: 'Cream',
    background_type: 'solid',
    background_color: '#fefce8',
    button_color: '#a16207',
    button_text_color: '#ffffff',
    text_color: '#713f12',
  },
  {
    name: 'Slate',
    background_type: 'solid',
    background_color: '#1e293b',
    button_color: '#38bdf8',
    button_text_color: '#0f172a',
    text_color: '#e2e8f0',
  },
  {
    name: 'Blush',
    background_type: 'solid',
    background_color: '#fff1f2',
    button_color: '#f43f5e',
    button_text_color: '#ffffff',
    text_color: '#9f1239',
  },
  {
    name: 'Olive',
    background_type: 'solid',
    background_color: '#f0fdf4',
    button_color: '#4d7c0f',
    button_text_color: '#ffffff',
    text_color: '#365314',
  },
  {
    name: 'Sunset',
    background_type: 'gradient',
    background_gradient_start: '#ff6b6b',
    background_gradient_end: '#feca57',
    button_color: '#ffffff',
    button_text_color: '#1a1a2e',
    text_color: '#ffffff',
  },
  {
    name: 'Ocean',
    background_type: 'gradient',
    background_gradient_start: '#667eea',
    background_gradient_end: '#764ba2',
    button_color: '#ffffff',
    button_text_color: '#667eea',
    text_color: '#ffffff',
  },
  {
    name: 'Forest',
    background_type: 'gradient',
    background_gradient_start: '#11998e',
    background_gradient_end: '#38ef7d',
    button_color: '#ffffff',
    button_text_color: '#11998e',
    text_color: '#ffffff',
  },
  {
    name: 'Midnight',
    background_type: 'gradient',
    background_gradient_start: '#232526',
    background_gradient_end: '#414345',
    button_color: '#00d9ff',
    button_text_color: '#000000',
    text_color: '#ffffff',
  },
  {
    name: 'Peach',
    background_type: 'gradient',
    background_gradient_start: '#ffecd2',
    background_gradient_end: '#fcb69f',
    button_color: '#e74c3c',
    button_text_color: '#ffffff',
    text_color: '#2c3e50',
  },
  {
    name: 'Azure',
    background_type: 'gradient',
    background_gradient_start: '#00c6ff',
    background_gradient_end: '#0072ff',
    button_color: '#ffffff',
    button_text_color: '#0072ff',
    text_color: '#ffffff',
  },
  {
    name: 'Neon',
    background_type: 'gradient',
    background_gradient_start: '#141e30',
    background_gradient_end: '#243b55',
    button_color: '#39ff14',
    button_text_color: '#101820',
    text_color: '#e5f9ff',
  },
  {
    name: 'Ruby',
    background_type: 'gradient',
    background_gradient_start: '#8e0e00',
    background_gradient_end: '#1f1c18',
    button_color: '#f94144',
    button_text_color: '#ffffff',
    text_color: '#ffe3e3',
  },
  {
    name: 'Skyline',
    background_type: 'gradient',
    background_gradient_start: '#4facfe',
    background_gradient_end: '#00f2fe',
    button_color: '#0f172a',
    button_text_color: '#ffffff',
    text_color: '#0f172a',
  },
  {
    name: 'Velvet',
    background_type: 'gradient',
    background_gradient_start: '#3f0d68',
    background_gradient_end: '#5f0f40',
    button_color: '#ffb703',
    button_text_color: '#2b0a3d',
    text_color: '#f8f2ff',
  },
  {
    name: 'Coral',
    background_type: 'gradient',
    background_gradient_start: '#ff9a9e',
    background_gradient_end: '#fad0c4',
    button_color: '#c0392b',
    button_text_color: '#ffffff',
    text_color: '#4a1a14',
  },
  {
    name: 'Twilight',
    background_type: 'gradient',
    background_gradient_start: '#0f0c29',
    background_gradient_end: '#302b63',
    button_color: '#e0aaff',
    button_text_color: '#10002b',
    text_color: '#e0aaff',
  },
  {
    name: 'Honey',
    background_type: 'gradient',
    background_gradient_start: '#f7971e',
    background_gradient_end: '#ffd200',
    button_color: '#2d1b00',
    button_text_color: '#ffd200',
    text_color: '#2d1b00',
  },
  {
    name: 'Arctic',
    background_type: 'gradient',
    background_gradient_start: '#e0eafc',
    background_gradient_end: '#cfdef3',
    button_color: '#1e3a5f',
    button_text_color: '#ffffff',
    text_color: '#1e3a5f',
  },
  {
    name: 'Cherry',
    background_type: 'solid',
    background_color: '#1c1017',
    button_color: '#ff2d55',
    button_text_color: '#ffffff',
    text_color: '#ffc0cb',
  },
  {
    name: 'Mocha',
    background_type: 'solid',
    background_color: '#3e2723',
    button_color: '#d7a86e',
    button_text_color: '#3e2723',
    text_color: '#efebe9',
  },

  // ── Special ──────────────────────────────────────────────────────────────
  {
    name: 'Black Gold',
    category: 'special',
    background_type: 'solid',
    background_color: '#0a0a0a',
    button_color: '#d4af37',
    button_text_color: '#0a0a0a',
    text_color: '#d4af37',
  },
  {
    name: 'Sakura',
    category: 'special',
    background_type: 'solid',
    background_color: '#fff0f3',
    button_color: '#ff4d88',
    button_text_color: '#ffffff',
    text_color: '#5d1b2e',
  },
  {
    name: 'Smoke',
    category: 'special',
    background_type: 'gradient',
    background_gradient_start: '#1c1c1e',
    background_gradient_end: '#2c2c2e',
    button_color: '#e0e0e0',
    button_text_color: '#1a1a1a',
    text_color: '#d4d4d4',
  },
  {
    name: 'Emerald Night',
    category: 'special',
    background_type: 'gradient',
    background_gradient_start: '#111827',
    background_gradient_end: '#064e3b',
    button_color: '#10b981',
    button_text_color: '#022c22',
    text_color: '#a7f3d0',
  },
  {
    name: 'Obsidian',
    category: 'special',
    background_type: 'gradient',
    background_gradient_start: '#0f0f0f',
    background_gradient_end: '#1a1a2e',
    button_color: '#6366f1',
    button_text_color: '#ffffff',
    text_color: '#e0e0ff',
  },
  {
    name: 'Champagne',
    category: 'special',
    background_type: 'solid',
    background_color: '#f5f0e8',
    button_color: '#b8960c',
    button_text_color: '#ffffff',
    text_color: '#5c4a00',
  },

];
